import { TLayoutItem } from '@redux/layouts/actions';
import { IRestColumnProps } from 'components/rest/Table/TableColumn';
import RestList from 'containers/rest/List';
import { useGetFilter } from 'hooks/useSearchParamsObj';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { DynamicObjectType, FilterFromUrlResponseType } from 'types/commons';
import { getColumns } from './columns';
import { TFilterItem } from 'components/rest/Filter/FilterRow';
import {
  EXCHANGE_CHANNEL,
  ORDER_EXCHANGE_STATUS,
  PAYMENT_STATUS_CONST,
} from 'constants/ordersExchange';
import PaymentMethodSelect from '../components/Filter/PaymentMethodSelect';
import RangePickerFilter from '../components/RangePickerFilter';
import { DATA_FIELD_SEARCH } from './utils';
import useGetSearchKey from 'hooks/useSearchKeys';
import { useDispatch } from '@redux';
import crudActions from '@redux/crudActions';
import dayjs from 'dayjs';
import { getQueryString } from 'api/utils';
import { values } from 'lodash';

const SEARCH_VALUE = {
  all: '',
  AWAITING_CONFIRMATION: ['AWAITING_CONFIRMATION'],
  CONFIRMED: ['CONFIRMED'],
  RETURNING: ['RETURNING'],
  PROCESSING: ['PROCESSING'],
  COMPLETED: ['COMPLETED'],
  CANCELLED: ['CANCELLED'],
};

const FILTERS_OBJECT = {
  AWAITING_CONFIRMATION: {
    condition: 'and',
    value: SEARCH_VALUE['AWAITING_CONFIRMATION'],
    operator: 'is',
    key: 'status',
  },
  CONFIRMED: {
    condition: 'and',
    value: SEARCH_VALUE['CONFIRMED'],
    operator: 'is',
    key: 'status',
  },
  RETURNING: {
    condition: 'and',
    value: SEARCH_VALUE['RETURNING'],
    operator: 'is',
    key: 'status',
  },
  PROCESSING: {
    condition: 'and',
    value: SEARCH_VALUE['PROCESSING'],
    operator: 'is',
    key: 'status',
  },
  COMPLETED: {
    condition: 'and',
    value: SEARCH_VALUE['COMPLETED'],
    operator: 'is',
    key: 'status',
  },
  CANCELLED: {
    condition: 'and',
    value: SEARCH_VALUE['CANCELLED'],
    operator: 'is',
    key: 'status',
  },
};

const INITIAL_LAYOUTS: TLayoutItem[] = [
  {
    rootPath: '/orders-exchange',
    pathId: 'AWAITING_CONFIRMATION',
    title: 'Awaiting Confirmation',
    customPath: `/orders-exchange?customLayout=AWAITING_CONFIRMATION&filters=${encodeURIComponent(
      JSON.stringify([FILTERS_OBJECT['AWAITING_CONFIRMATION']], null, 0),
    )}`,
    isDefault: true,
  },
  {
    rootPath: '/orders-exchange',
    pathId: 'CONFIRMED',
    title: 'Confirmed',
    customPath: `/orders-exchange?customLayout=CONFIRMED&filters=${encodeURIComponent(
      JSON.stringify([FILTERS_OBJECT['CONFIRMED']], null, 0),
    )}`,
    isDefault: true,
  },
  {
    rootPath: '/orders-exchange',
    pathId: 'RETURNING',
    title: 'Returning',
    customPath: `/orders-exchange?customLayout=RETURNING&filters=${encodeURIComponent(
      JSON.stringify([FILTERS_OBJECT['RETURNING']], null, 0),
    )}`,
    isDefault: true,
  },
  {
    rootPath: '/orders-exchange',
    pathId: 'PROCESSING',
    title: 'Processing',
    customPath: `/orders-exchange?customLayout=PROCESSING&filters=${encodeURIComponent(
      JSON.stringify([FILTERS_OBJECT['PROCESSING']], null, 0),
    )}`,
    isDefault: true,
  },
  {
    rootPath: '/orders-exchange',
    pathId: 'COMPLETED',
    title: 'Completed',
    customPath: `/orders-exchange?customLayout=COMPLETED&filters=${encodeURIComponent(
      JSON.stringify([FILTERS_OBJECT['COMPLETED']], null, 0),
    )}`,
    isDefault: true,
  },
  {
    rootPath: '/orders-exchange',
    pathId: 'CANCELLED',
    title: 'Cancelled',
    customPath: `/orders-exchange?customLayout=CANCELLED&filters=${encodeURIComponent(
      JSON.stringify([FILTERS_OBJECT['CANCELLED']], null, 0),
    )}`,
    isDefault: true,
  },
];

const JOIN_INITIALFILTER = [
  'newOrder',
  'orderReturn',
  'originalOrder',
  'customer',
  'newOrder.payments',
];

const List = ({ initialFilter }: { initialFilter?: DynamicObjectType }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { searchParams, filter } = useGetFilter();
  const { searchKeys } = useGetSearchKey(DATA_FIELD_SEARCH);

  const restInitialFilter: FilterFromUrlResponseType = useMemo(
    () => ({
      join: JOIN_INITIALFILTER,
      orderBy: 'createdAt,ASC',
      filters: [
        ...(initialFilter ? [initialFilter] : []),
        searchParams.get('status')
          ? {
              condition: 'and',
              key: 'status',
              operator: 'is',
              value: SEARCH_VALUE[searchParams.get('status')],
            }
          : {},
        !filter?.filters?.find((item) => item.key === 'createdAt')
          ? {
              condition: 'and',
              key: 'createdAt',
              operator: 'gte',
              value: dayjs()
                .subtract(1, 'month')
                .startOf('month')
                .toISOString(),
            }
          : {},
        !filter?.filters?.find((item) => item.key === 'createdAt')
          ? {
              condition: 'and',
              key: 'createdAt',
              operator: 'lte',
              value: dayjs().endOf('day').toISOString(),
            }
          : {},
      ],
    }),
    [initialFilter, searchParams],
  );

  const filters: TFilterItem[] = useMemo(
    () => [
      {
        key: 'status',
        label: 'Status',
        dataSource: Object.values(ORDER_EXCHANGE_STATUS)?.map(
          (e: DynamicObjectType) => ({
            value: e.value,
            label: t(e.text),
          }),
        ),
      },
      {
        key: 'newOrder.paymentStatus',
        label: 'Payment status',
        dataSource: Object.values(PAYMENT_STATUS_CONST).map(
          (e: DynamicObjectType) => ({
            value: e.value,
            label: t(e.text),
          }),
        ),
      },
      {
        key: 'newOrder.paymentType',
        label: 'Payment method',
        valueCPN: <PaymentMethodSelect />,
      },
      {
        key: 'exchangeType',
        label: 'Order type',
        dataSource: Object.values(EXCHANGE_CHANNEL),
      },
      {
        key: 'createdAt',
        label: 'Order created time',
        isDatePicker: true,
      },
      {
        key: 'cancelledAt',
        label: 'Order cancelled time',
        isDatePicker: true,
      },
      {
        key: 'confirmedAt',
        label: 'Order confirmed time',
        isDatePicker: true,
      },
      {
        key: 'processedAt',
        label: 'Order processed time',
        isDatePicker: true,
      },
      {
        key: 'completedAt',
        label: 'Order completed time',
        isDatePicker: true,
      },
      {
        key: 'returnedAt',
        label: 'Order returned time',
        isDatePicker: true,
      },
    ],
    [],
  );

  const _columns = useMemo(() => {
    return getColumns(searchParams.get('customLayout')) as IRestColumnProps[];
  }, [searchParams.get('customLayout')]);

  useEffect(() => {
    dispatch(crudActions['ordersExchange'].clearCurrent());
  }, [window.location.pathname]);

  const dateFilters = useMemo(
    () =>
      values({
        ...restInitialFilter.filters,
        ...filter?.filters,
      }).reduce(
        (acc: DynamicObjectType, filter: DynamicObjectType) => {
          if (filter.key === 'createdAt' && filter.operator === 'gte') {
            acc.startDate = filter.value;
          }
          if (filter.key === 'createdAt' && filter.operator === 'lte') {
            acc.endDate = filter.value;
          }
          return acc;
        },
        { startDate: null, endDate: null },
      ),
    [restInitialFilter, filter],
  );

  const exportExcelQuery = useCallback(() => {
    const requestParams = getQueryString(dateFilters);
    return `/orders-exchange/export?${requestParams?.replace('page=1&limit=10', '')}`;
  }, [dateFilters]);

  return (
    <RestList
      initialFilter={restInitialFilter}
      columns={_columns}
      model="ordersExchange"
      filters={filters.filter((item) => !!item?.key)}
      gotoCreatePage={() => navigate('/orders-exchange/create')}
      searchKeys={searchKeys}
      extraActionFilters={<RangePickerFilter />}
      placeholderInputSearch="Search by Exchange No, Return No, Order No"
      rowSelectionActions={<></>}
      hasImport={false}
      hasExport
      hasCustomLayout
      initialLayouts={INITIAL_LAYOUTS}
      dataFieldSearch={DATA_FIELD_SEARCH}
      exportExcelQuery={`${exportExcelQuery()}`}
      excelName={`Export_exchange_orders_${dayjs().format('YYYY_MM_DD')}`}
    />
  );
};

export default List;
